import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import algoliasearch from "algoliasearch";
import { InstantSearch, Hits } from "react-instantsearch";
import Authenticated from "../components/Layouts/Authenticated";
import { FiltersIcon } from "../components/ui";
import {
  CustomPagination,
  DirectoryFilters,
  SearchBox,
  SearchResult,
} from "../components/UserDirectory";
import { theme } from "../theme";
import { userDirectoryStyles } from "./styles";

/**
 * UserDirectory Page Component
 *
 * A searchable directory of users with filtering capabilities.
 * Uses Algolia InstantSearch for real-time search functionality.
 */
const UserDirectory: React.FC = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [filtersOpen, setFiltersOpen] = useState(!isMobile);

  // Update filters visibility based on screen size
  useEffect(() => {
    setFiltersOpen(!isMobile);
  }, [isMobile]);

  // Initialize Algolia search client
  const searchClient = algoliasearch(
    process.env.REACT_APP_ALGOLIA_APP_ID || "",
    process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY || ""
  );
  const indexName = process.env.REACT_APP_ALGOLIA_INDEX || "users";

  const toggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  };

  return (
    <Authenticated>
      <title>User Directory - Materiom Commons</title>
      <Container maxWidth="xl" sx={userDirectoryStyles.container}>
        <InstantSearch searchClient={searchClient} indexName={indexName}>
          <Grid container spacing={3}>
            {/* Filters Panel */}
            <Grid size={{ xs: 12, lg: 3 }}>
              <Paper
                elevation={userDirectoryStyles.paper.elevation}
                sx={{
                  borderRadius: userDirectoryStyles.paper.borderRadius,
                  overflow: userDirectoryStyles.paper.overflow,
                }}
              >
                {/* Filters Header */}
                <Box
                  sx={{
                    ...userDirectoryStyles.filtersHeader,
                    cursor: isMobile ? "pointer" : "default",
                  }}
                  onClick={isMobile ? toggleFilters : undefined}
                >
                  <Box sx={userDirectoryStyles.filtersHeaderContent}>
                    <FiltersIcon sx={userDirectoryStyles.filtersIcon} />
                    <Box sx={userDirectoryStyles.filtersHeaderRow}>
                      <Typography
                        variant="h6"
                        sx={userDirectoryStyles.filtersTitle}
                      >
                        Filters
                      </Typography>
                      {isMobile && (
                        <Typography
                          variant="body2"
                          sx={userDirectoryStyles.filtersToggleText}
                        >
                          {filtersOpen ? "Hide" : "Show"} filters
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Filters Content */}
                <Collapse in={filtersOpen}>
                  <Box sx={userDirectoryStyles.filtersContent}>
                    {/* Search Box */}
                    <Box sx={userDirectoryStyles.keywordSearchSection}>
                      <Typography
                        variant="subtitle2"
                        sx={userDirectoryStyles.keywordSearchTitle}
                      >
                        Keyword Search
                      </Typography>
                      <SearchBox
                        placeholder="Type keyword or location"
                        sx={userDirectoryStyles.keywordSearchBox}
                      />
                    </Box>

                    {/* Directory Filters */}
                    <DirectoryFilters />
                  </Box>
                </Collapse>
              </Paper>
            </Grid>

            {/* Results Panel */}
            <Grid size={{ xs: 12, lg: 9 }}>
              <Paper
                elevation={userDirectoryStyles.paper.elevation}
                sx={{
                  borderRadius: userDirectoryStyles.paper.borderRadius,
                  overflow: userDirectoryStyles.paper.overflow,
                }}
              >
                {/* Results Header */}
                <Box sx={userDirectoryStyles.resultsHeader}>
                  <Typography
                    variant="h5"
                    sx={userDirectoryStyles.resultsTitle}
                  >
                    User Directory
                  </Typography>
                </Box>

                {/* Results Content */}
                <Box sx={userDirectoryStyles.resultsContent}>
                  <Hits hitComponent={SearchResult} />
                </Box>

                {/* Pagination */}
                <Box sx={userDirectoryStyles.paginationContainer}>
                  <CustomPagination />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </InstantSearch>
      </Container>
    </Authenticated>
  );
};

export default UserDirectory;
